import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Chip, Stack, TextField, MenuItem,
  FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Divider, Tooltip, CircularProgress,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  Traffic as TrafficIcon, Refresh as RefreshIcon, WarningAmber as WarningIcon,
  ReportProblem as ReportProblemIcon, ErrorOutline as ErrorIcon,
  Close as CloseIcon, Visibility as VisibilityIcon, Security as SecurityIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { trafficApi, TrafficEntry, TrafficStatistics } from '../services/trafficApi';
import { useLanguage } from '../contexts/LanguageContext';
import { blobticMessage } from '../components/BlobticMessage';
import GenericPage from '../components/GenericPage';
import GenericTable from '../components/GenericTable';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

function statusColor(status: number): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status >= 500) return 'error';
  if (status === 429 || status === 401) return 'warning';
  if (status >= 400) return 'warning';
  return 'success';
}

function methodColor(method: string): 'info' | 'success' | 'warning' | 'error' | 'secondary' | 'default' {
  switch (method.toUpperCase()) {
    case 'GET': return 'info';
    case 'POST': return 'success';
    case 'PUT': case 'PATCH': return 'warning';
    case 'DELETE': return 'error';
    default: return 'default';
  }
}

function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${ms} ms`;
}

function formatDate(value: string): string {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-DZ');
  } catch {
    return value;
  }
}

function AnomalyChip({ flags }: { flags?: string | null }) {
  if (!flags) return <Typography variant="caption" color="text.secondary">-</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {flags.split(',').map(f => (
        <Chip key={f} label={f} size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
      ))}
    </Stack>
  );
}

function CountryChip({ country }: { country?: string | null }) {
  if (!country) return <Typography variant="caption" color="text.secondary">-</Typography>;
  const code = country.toUpperCase();
  const flag = code === 'DZ' ? '🇩🇿 ' : '';
  return <Chip label={`${flag}${code}`} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }} />;
}

const TrafficCard: React.FC<{ entry: TrafficEntry; onDetail: () => void; t: (k: string, ...a: any[]) => string }> = ({ entry, onDetail, t }) => {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={entry.TRAF_METHOD} size="small" color={methodColor(entry.TRAF_METHOD)} sx={{ fontSize: '0.7rem', height: 20 }} />
            <Chip label={String(entry.TRAF_STATUS)} size="small" color={statusColor(entry.TRAF_STATUS)} sx={{ fontSize: '0.7rem', height: 20 }} />
            {entry.TRAF_IS_ANOMALY && <Chip label={t('traffic.anomaly')} size="small" color="error" sx={{ fontSize: '0.65rem', height: 20 }} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">{formatDate(entry.TRAF_DATE)}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{entry.TRAF_PATH}{entry.TRAF_QUERY ? `?${entry.TRAF_QUERY}` : ''}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={formatDuration(entry.TRAF_DURATION_MS)} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
          {entry.TRAF_IP && <Chip label={entry.TRAF_IP} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
          {entry.TRAF_COUNTRY && <CountryChip country={entry.TRAF_COUNTRY} />}
          {entry.TRAF_USER_EMAIL && <Chip label={entry.TRAF_USER_EMAIL} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />}
          <Chip
            label={entry.TRAF_SOURCE === 'public' ? 'public' : 'interne'}
            size="small"
            color={entry.TRAF_SOURCE === 'public' ? 'info' : 'default'}
            variant={entry.TRAF_SOURCE === 'public' ? 'outlined' : 'filled'}
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Stack>
        {entry.TRAF_ANOMALY_FLAGS && <AnomalyChip flags={entry.TRAF_ANOMALY_FLAGS} />}
        <Button size="small" variant="outlined" color="primary" startIcon={<VisibilityIcon fontSize="small" />} onClick={onDetail} fullWidth>
          {t('traffic.details')}
        </Button>
      </Stack>
    </Paper>
  );
};

export const TrafficPage: React.FC = () => {
  const { t } = useLanguage();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stats, setStats] = useState<TrafficStatistics | null>(null);
  const [entries, setEntries] = useState<TrafficEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState<number>(24);
  const [anomaliesOnly, setAnomaliesOnly] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selected, setSelected] = useState<TrafficEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trafficApi.select({ hours, limit: 200, anomaliesOnly, status: statusFilter || undefined });
      setStats(result.statistics);
      setEntries(result.data || []);
    } catch (e: any) {
      blobticMessage.error(t('traffic.error_load'), e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [hours, anomaliesOnly, statusFilter, blobticMessage.error, t]);

  useEffect(() => {
    load();
  }, [load]);

  const statCards: { label: string; value: number | string; icon: React.ReactNode; color: string }[] = [
    { label: t('traffic.stats.total'), value: stats?.total_requests ?? 0, icon: <TrafficIcon />, color: '#3b82f6' },
    { label: t('traffic.stats.anomalies'), value: stats?.total_anomalies ?? 0, icon: <WarningIcon />, color: '#ef4444' },
    { label: t('traffic.stats.server_errors'), value: stats?.server_errors ?? 0, icon: <ErrorIcon />, color: '#dc2626' },
    { label: t('traffic.stats.auth_failures'), value: stats?.auth_failures ?? 0, icon: <SecurityIcon />, color: '#f59e0b' },
    { label: t('traffic.stats.not_found'), value: stats?.not_found ?? 0, icon: <ReportProblemIcon />, color: '#8b5cf6' },
    { label: t('traffic.stats.rate_limited'), value: stats?.rate_limited ?? 0, icon: <WarningIcon />, color: '#f97316' },
    { label: t('traffic.stats.avg_duration'), value: stats ? formatDuration(stats.avg_duration_ms) : '-', icon: <TrafficIcon />, color: '#06b6d4' },
    { label: t('traffic.stats.distinct_ips'), value: stats?.distinct_ips ?? 0, icon: <SecurityIcon />, color: '#64748b' },
    { label: t('traffic.stats.distinct_countries'), value: stats?.distinct_countries ?? 0, icon: <PublicIcon />, color: '#10b981' },
  ];

  return (
    <GenericPage
      title={t('traffic.title')}
      icon={<TrafficIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ borderRadius: 2 }}>
            {t('traffic.refresh')}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {statCards.map(card => (
          <Grid item xs={6} sm={3} lg={1.5} key={card.label}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5, borderRadius: 3, height: '100%', textAlign: 'center',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'
              }}
            >
              <Box sx={{ color: card.color, display: 'flex', justifyContent: 'center', mb: 0.5 }}>{card.icon}</Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1 }}>{card.value}</Typography>
              <Typography variant="caption" color="text.secondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label={t('traffic.filter.period')}
              select
              size="small"
              fullWidth
              sx={fsx}
              value={hours}
              onChange={e => setHours(Number(e.target.value))}
            >
              <MenuItem value={1}>{t('traffic.filter.last_hour')}</MenuItem>
              <MenuItem value={24}>{t('traffic.filter.last_24h')}</MenuItem>
              <MenuItem value={168}>{t('traffic.filter.last_7d')}</MenuItem>
              <MenuItem value={720}>{t('traffic.filter.last_30d')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" sx={fsx}>
              <InputLabel>{t('traffic.filter.status')}</InputLabel>
              <Select value={statusFilter} label={t('traffic.filter.status')} onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="">{t('traffic.filter.all')}</MenuItem>
                <MenuItem value="401">401</MenuItem>
                <MenuItem value="403">403</MenuItem>
                <MenuItem value="404">404</MenuItem>
                <MenuItem value="429">429</MenuItem>
                <MenuItem value="500">500</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant={anomaliesOnly ? 'contained' : 'outlined'}
              color={anomaliesOnly ? 'error' : 'inherit'}
              startIcon={<WarningIcon />}
              onClick={() => setAnomaliesOnly(v => !v)}
              sx={{ borderRadius: 2 }}
            >
              {t('traffic.filter.anomalies_only')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              label={t('traffic.filter.count', entries.length)}
              color="primary" variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : isMobile ? (
        <Box>
          {entries.map(entry => (
            <TrafficCard
              key={entry.TRAF_IDF}
              entry={entry}
              t={t}
              onDetail={() => setSelected(entry)}
            />
          ))}
        </Box>
      ) : (
        <GenericTable
          columns={[
            { id: 'TRAF_DATE', label: t('traffic.table.date'), minWidth: 150, format: (v: any) => formatDate(v) },
            {
              id: 'TRAF_METHOD', label: t('traffic.table.method'), minWidth: 90,
              format: (v: any) => <Chip label={v} size="small" color={methodColor(v)} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
            },
            {
              id: 'TRAF_PATH', label: t('traffic.table.path'), minWidth: 260,
              format: (v: any, row?: any) => (
                <Box>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{v}</Typography>
                  {row?.TRAF_QUERY && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>?{row.TRAF_QUERY}</Typography>}
                </Box>
              )
            },
            {
              id: 'TRAF_STATUS', label: t('traffic.table.status'), minWidth: 90,
              format: (v: any) => <Chip label={v} size="small" color={statusColor(v)} sx={{ fontSize: '0.7rem', height: 20 }} />
            },
            {
              id: 'TRAF_DURATION_MS', label: t('traffic.table.duration'), minWidth: 100,
              format: (v: any) => {
                const slow = v >= 5000;
                return <Typography variant="body2" color={slow ? 'error' : 'inherit'} fontWeight={slow ? 700 : 400}>{formatDuration(v)}</Typography>;
              }
            },
            { id: 'TRAF_IP', label: t('traffic.table.ip'), minWidth: 120, format: (v: any) => v || '-' },
            { id: 'TRAF_COUNTRY', label: t('traffic.table.country'), minWidth: 90, format: (v: any) => <CountryChip country={v} /> },
            { id: 'TRAF_USER_EMAIL', label: t('traffic.table.user'), minWidth: 160, format: (v: any) => v || '-' },
            {
              id: 'TRAF_SOURCE', label: t('traffic.table.source'), minWidth: 90,
              format: (v: any) => v === 'public' ? <Chip label="public" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} /> : <Chip label="interne" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
            },
            {
              id: 'TRAF_REQUEST_HEADERS', label: t('traffic.table.headers'), minWidth: 200,
              format: (v: any) => v ? (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-all' }}>
                  {v}
                </Typography>
              ) : '-'
            },
            { id: 'TRAF_ANOMALY_FLAGS', label: t('traffic.table.anomalies'), minWidth: 200, format: (v: any) => <AnomalyChip flags={v} /> },
          ]}
          data={entries}
          searchPlaceholder={t('traffic.search_placeholder')}
          rowActions={(row) => (
            <Tooltip title={t('traffic.details')}>
              <IconButton size="small" onClick={() => setSelected(row)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          maxHeight="calc(100vh - 480px)"
        />
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {t('traffic.details_title')}
            {selected?.TRAF_IS_ANOMALY && <Chip label={t('traffic.anomaly')} size="small" color="error" />}
          </Stack>
          <IconButton size="small" onClick={() => setSelected(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`#${selected.TRAF_IDF}`} size="small" variant="outlined" />
                <Chip label={selected.TRAF_METHOD} size="small" color={methodColor(selected.TRAF_METHOD)} />
                <Chip label={String(selected.TRAF_STATUS)} size="small" color={statusColor(selected.TRAF_STATUS)} />
                <Chip label={formatDuration(selected.TRAF_DURATION_MS)} size="small" variant="outlined" />
                <Chip label={selected.TRAF_IP || '-'} size="small" variant="outlined" />
                {selected.TRAF_COUNTRY && <CountryChip country={selected.TRAF_COUNTRY} />}
                {selected.TRAF_USER_EMAIL && <Chip label={selected.TRAF_USER_EMAIL} size="small" color="primary" variant="outlined" />}
              </Stack>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <b>{selected.TRAF_METHOD}</b> {selected.TRAF_PATH}{selected.TRAF_QUERY ? `?${selected.TRAF_QUERY}` : ''}
              </Typography>
              {selected.TRAF_ANOMALY_FLAGS && (
                <Box>
                  <Typography variant="subtitle2" color="error">{t('traffic.anomaly_flags')}</Typography>
                  <AnomalyChip flags={selected.TRAF_ANOMALY_FLAGS} />
                  {selected.TRAF_ANOMALY_DETAIL && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{selected.TRAF_ANOMALY_DETAIL}</Typography>
                  )}
                </Box>
              )}
              {selected.TRAF_REQUEST_HEADERS && (
                <Box>
                  <Typography variant="subtitle2">{t('traffic.request_headers')}</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default', maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {selected.TRAF_REQUEST_HEADERS}
                    </Typography>
                  </Paper>
                </Box>
              )}
              {selected.TRAF_REQUEST_BODY && (
                <Box>
                  <Typography variant="subtitle2">{t('traffic.request_body')}</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default', maxHeight: 220, overflow: 'auto' }}>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {selected.TRAF_REQUEST_BODY}
                    </Typography>
                  </Paper>
                </Box>
              )}
              <Divider />
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t('traffic.table.user_agent')}</Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{selected.TRAF_USER_AGENT || '-'}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t('traffic.correlation_id')}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{selected.TRAF_CORRELATION_ID || '-'}</Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} color="inherit">{t('traffic.close')}</Button>
        </DialogActions>
      </Dialog>
    </GenericPage>
  );
};

export default TrafficPage;
